import 'tm3-core/api/mobx/current-user';

import ProntoCache from 'bz-pronto-core/cache';

import DashboardStore from 'bz-stores/lib/dashboard-store';

import { createContextForClass } from 'bz-utilities/hooks/context-v2';
import history from 'bz-utilities/modules/networking/history/rr4';

import { action, computed, observable, runInAction } from 'mobx';

import Api from 'tm3-core/api';
import LocationResponse from 'tm3-core/api/mobx/locations-response';
import {
  AutoLoginRequest,
  ChangeLoggedInLocation,
  CurrentUser,
  GetCurrentUser,
  ReferralPortalCurrentUserRequest
} from 'tm3-core/api.dtos';
import { cache } from 'tm3-core/cache';

import Config from './config';
import DiaryViewStore from './diary-view-store';
import JsonServiceClient from './JsonServiceClient';
import Licence from './licence';
import Locations from './locations';
import Locks from './locks';
import Lookups from './lookups';
import ReminderStore from './reminder-store';
import Security from './security';
import Templates from './templates';
import Tours from './tours';
import ToursMobx from './tours-mobx';

export enum AuthStoreMode {
  none = 'none',
  countdown = 'countdown',
  reAuth = 're-auth'
}
var lowerRegex = /[a-z]/g;

const toCurrentUser = (result: CurrentUser): CurrentUser => {
  const user = result instanceof CurrentUser ? result : new CurrentUser(result as CurrentUser);
  user.lastName ??= '';
  return user;
};

export class AuthStoreType {
  @observable authenticated = false;
  @observable user: CurrentUser = this.initialUser;
  @computed get initials() {
    const name = this.user?.displayName ?? '?';
    return name.replace(lowerRegex, '');
  }
  @observable authTimeout: number = 20;
  @observable mode: AuthStoreMode = AuthStoreMode.none;
  @observable stage1Authenticated: boolean = false;
  @observable usedLicenses: number = 0;
  // Unified seat entitlement (Cache.getSeatEntitlement) - resolved server-side from the live
  // licence (modular seat count when migrated, legacy License fields otherwise). Consumers
  // should branch on licenceType ('User' | 'Diary' | 'Fixed'), never on product name.
  @observable licenceType: string = '';
  @observable seatLimit: number = 0;
  @observable isModularLicence: boolean = false;
  @observable taskCount: number = 0;

  private client = new JsonServiceClient();

  regex = /^\/login|^\/help/;

  ttl?: number;

  protected get initialUser(): CurrentUser {
    return new CurrentUser({
      location: new LocationResponse()
    });
  }

  public setAuthToken(token: string): void {
    sessionStorage.setItem('authToken', token);
  }

  public setTimeoutAmount(mins: number): void {
    this.authTimeout = mins < 1 ? 20 : mins;
    this.onReset();
  }

  @action.bound
  public onReset(): void {
    this.mode = AuthStoreMode.none;
    clearTimeout(this.ttl);
    let sec = this.authTimeout * 60000 - 30000;
    this.ttl = window.setTimeout(this.onExpiring, sec);
  }

  @action.bound
  private onExpiring(): void {
    let { pathname } = window.location;
    if (this.regex.test(pathname)) {
      return;
    }

    const tabLastActive = localStorage.getItem('tabLastActive') ?? '';

    if (tabLastActive != '') {
      const lastActive = new Date(tabLastActive);
      const seconds = (new Date().getTime() - lastActive.getTime()) / 1000;
      const timeoutSeconds = this.authTimeout * 60 - 30;

      if (document.visibilityState !== 'visible' && !(seconds > timeoutSeconds)) {
        this.onReset();
        return;
      }
    }

    this.mode = AuthStoreMode.countdown;
    return;
  }

  @action.bound
  public async checkReferralPortalLoginState() {
    try {
      const { result } = await this.client.get(new ReferralPortalCurrentUserRequest());
      this.onAuthenticate(result);
    } catch (ex: any) {
      if (window.location.pathname !== '/wrong-address' && ex.status !== 404) {
        history.replace(this.getReferralPortalLoginRedirect());
      }
    }
  }

  @action.bound
  public async refreshReferralPortalUser() {
    try {
      const { result } = await this.client.get(new ReferralPortalCurrentUserRequest());
      runInAction(() => {
        this.user = toCurrentUser(result);
        if (result.meta) this.setTimeoutAmount(result.meta['autoLogout'].toInt());
      });
    } catch (ex: any) {
      if (window.location.pathname !== '/wrong-address' && ex.status !== 404) {
        history.replace(this.getReferralPortalLoginRedirect());
      }
    }
  }

  @action.bound
  async refreshCurrentUser() {
    const result = await this.client.get(new GetCurrentUser());
    this.user = toCurrentUser(result);
  }

  @action.bound
  async checkLoginState() {
    try {
      const result = await this.client.get(new GetCurrentUser());
      this.onAuthenticate(result);
      this.initialiseStores();
    } catch (ex) {
      if (window.location.pathname !== '/wrong-address' && ex.status !== 404) {
        history.replace('/login');
      }
    }
  }
  @action.bound
  async changeLocation(id: number) {
    const res = await this.client.post(new ChangeLoggedInLocation({ id }));
    this.onAuthenticate(res);
    this.initialiseStores();

    Security.actions.updateDefaultLocation(res.location.id);
    DashboardStore.setLocationUserId(res.location, res.practitionerId);
  }

  @action.bound
  initialiseStores() {
    const { user } = this;

    if (cache.app.ReferralPortal) {
      return;
    }

    Config.actions.loadAll().then((config) => {
      this.setMode();
      this.setTimeoutAmount(parseInt(config[0].AUTOLOGOUTINTERVAL));
    });

    this.initIntercom();
    Tours.actions.preload(user.tours);
    Object.assign(ToursMobx.tours, user.tours);
    Locks.actions.clearAllForUser();
    Security.actions.loadAll();
    DashboardStore.setLocationUserId(user.location, user.practitionerId);
    Security.actions.updateDefaultLocation(user.location.id);
    Lookups.actions.loadAll();
    Lookups.actions.loadRegionalLookups();
    Templates.actions.loadAll();
    Locations.actions.load();
    ReminderStore.getReminderTime();
    ProntoCache.onConfigure();
    DiaryViewStore.onReload(user.id);
  }

  private getReferralPortalLoginRedirect(): string {
    const path = `${window.location.pathname}${window.location.search}${window.location.hash}`;
    if (path.startsWith('/login')) {
      return '/login';
    }
    return `/login?redirectTo=${encodeURIComponent(path)}`;
  }

  @action.bound
  setMode() {
    const mode = Config.get('config')?.TM3MODE;
    if (mode && mode.toUpperCase() === 'TRUE') {
      const configDictionary = {
        patient: {
          normal: 'Client',
          normalPlural: 'Clients',
          possessive: "Client's",
          possessivePlural: "Clients'"
        },
        group: {
          normal: 'Business',
          normalPlural: 'Businesses',
          possessive: "Business'",
          possessivePlural: "Business'"
        },
        vat: {
          aus: 'GST',
          can: 'GST',
          gbr: 'VAT',
          eur: 'VAT',
          gcc: 'VAT'
        }
      };
      // build up plurals and possessives as lower case keys

      const result = [];
      for (let key in configDictionary) {
        const { normal } = configDictionary[key];
        if (normal) {
          if (!configDictionary[key].normalPlural) {
            configDictionary[key].normalPlural = normal + 's';
          }
          if (!configDictionary[key].possessive) {
            configDictionary[key].possessive = normal + "'s";
          }
          if (!configDictionary[key].possessivePlural) {
            result.push(
              (configDictionary[key].possessivePlural = configDictionary[key].normalPlural + "'")
            );
          } else {
            result.push(undefined);
          }
        } else {
          result.push(undefined);
        }
      }
      return result;
    }
  }

  @action.bound
  async logout(storeReset = true) {
    await Api.post('/api/auth/logout', {}).catch((err) => {});
    localStorage.diaryAlertsAcknowledgedIds = null;

    localStorage.setItem('tm3-logout', new Date());
    sessionStorage.clear();

    DiaryViewStore.onUnload();
    if (storeReset) {
      return Promise.all([Security.actions.logout(), Templates.reset()]).then(() => {
        this.stage1Authenticated = false;
        this.authenticated = false;
        this.user = this.initialUser;
      });
    }
  }

  get intercomAppId() {
    if (cache.isClick) return 'qqcol12v';
    return 'f8r88klr';
  }

  @action.bound
  async initIntercom(): Promise<void> {
    try {
      const { user } = this;
      const licensePromise = Licence.actions.load();
      if (localStorage.getItem('hideChat') == 'true') return;

      if (!cache.app.Caseflow) {
        await licensePromise;
        const host = window.location.host;

        let intercomSettings;

        if (!!process.env.isClick) {
          intercomSettings = {
            app_id: 'qqcol12v',
            name: user.displayName,
            ClientID: user.clientId,
            userName: user.name,
            email: user.email,
            subdomain: host.substring(0, host.indexOf('.')),
            Evaluating: !!Licence.get('licence').isEvaluating,
            isProntoPortal: !!Licence.get('licence').isProntoPortal,
            host: host,
            custom_launcher_selector: '#intercom_messenger',
            alignment: 'left',
            hide_default_launcher: true
          };
        } else {
          intercomSettings = {
            app_id: this.intercomAppId,
            name: user.displayName,
            ClientID: user.clientId,
            userName: user.name,
            email: user.email,
            subdomain: host.substring(0, host.indexOf('.')),
            Evaluating: !!Licence.get('licence').isEvaluating,
            isProntoPortal: !!Licence.get('licence').isProntoPortal,
            host: host,
            horizontal_padding: 60,
            alignment: 'left',
            custom_launcher_selector: '#tm3_intercom_messenger',
            hide_default_launcher: true
          };
        }
        window.Intercom?.('update', intercomSettings);
      }
    } catch (e) {
      console.error('[initIntercom] - something went wrong');
      console.error(e);
    }
  }

  @action.bound
  async autoLogin(token, redirectParam) {
    try {
      const result = await this.client.post(new AutoLoginRequest({ token }));
      if (!result) {
        return history.push('/help');
      } else {
        this.onAuthenticate(result);
        this.initialiseStores();
        if (process.env.isClick && !!redirectParam) {
          const url = decodeURIComponent(redirectParam);
          return history.push(url);
        }
        return history.push('/');
      }
    } catch {
      return history.push('/help');
    }
  }

  @action.bound
  onAuthenticate(
    result: CurrentUser,
    skipRedirects: boolean = false,
    isLogin: boolean = false
  ): boolean {
    this.stage1Authenticated = true;
    this.user = toCurrentUser(result);
    if (result.meta && result.meta['autoLogout'])
      this.setTimeoutAmount(result.meta['autoLogout'].toInt());
    if (this.user.practitionerId) {
      this.user.token = this.user.practitioner.physitrackToken;
    }
    if (skipRedirects) return true;

    if (result.twoFactorRequired && !result.twoFactorAuthenticated) {
      if (result.twoFactorSetup) history.push('/login/mfa/challenge');
      else history.push('/login/mfa/setup');
      return false;
    } else if (result.requiresUserRegistration) {
      history.push('/login/verify');
      return false;
    } else if (result.isAuthenticated) {
      localStorage.setItem('isFirstLoggedIn', String(true));
      localStorage.setItem('isAuthenticated', String(true));
    }
    this.authenticated = result.isAuthenticated;
    if (isLogin) localStorage.removeItem(`${result.userId}date`);

    return true;
  }
}

export const [AuthStoreContext, useAuthStore] = createContextForClass(AuthStoreType);

const AuthStore = new AuthStoreType();
export default AuthStore;
